/**
 * Centralized theme configuration for flat-neon arcade style
 * Based on visual-style-guide.md
 */

export const THEME = {
  // Color palette - flat neon arcade aesthetic
  colors: {
    // Primary neon colors
    primary: '#00F6FF',      // Neon Cyan - main highlight
    primaryHex: 0x00f6ff,
    primaryDark: '#00C4CC',
    primaryDarkHex: 0x00c4cc,

    // Accent colors
    cyan: '#00F6FF',
    cyanHex: 0x00f6ff,
    magenta: '#FF2EAF',
    magentaHex: 0xff2eaf,
    magentaDark: '#CC2090',
    magentaDarkHex: 0xcc2090,
    yellow: '#F9F871',
    yellowHex: 0xf9f871,
    green: '#3DFF92',
    greenHex: 0x3dff92,
    greenDark: '#2ECC71',
    greenDarkHex: 0x2ecc71,

    // Hit quality colors
    hit: {
      perfect: '#F9F871',    // Accent Yellow
      perfectHex: 0xf9f871,
      good: '#3DFF92',       // Accent Green
      goodHex: 0x3dff92,
      ok: '#00F6FF',         // Neon Cyan
      okHex: 0x00f6ff,
      bad: '#FF3355',        // Error Red
      badHex: 0xff3355,
    },

    // UI colors - deep bluish-purple arcade background
    background: '#050716',   // BG Base
    backgroundHex: 0x050716,
    backgroundLight: '#0B1024', // BG Alt Strip
    backgroundLightHex: 0x0b1024,
    backgroundPanel: '#24113F', // Grid Lines / panels
    backgroundPanelHex: 0x24113f,

    // Border colors
    border: '#24113F',
    borderHex: 0x24113f,
    borderLight: '#3A2060',
    borderLightHex: 0x3a2060,

    // Text colors
    text: '#FDFDFD',         // Text Bright
    textMuted: '#A8B5FF',    // Text Muted
    textDark: '#6B7AAA',
    textOnPrimary: '#050716',

    // Shadow
    shadowDeep: '#02030B',
    shadowDeepHex: 0x02030b,

    // Difficulty colors
    difficulty: {
      easy: '#3DFF92',       // Green
      medium: '#F9F871',     // Yellow
      hard: '#FF2EAF',       // Magenta
      expert: '#FF3355',     // Red
    },
  },

  // Font configuration - Orbitron for headings, Rajdhani for body
  fonts: {
    heading: 'Orbitron, sans-serif',
    headingBold: 'Orbitron, sans-serif',
    body: 'Rajdhani, sans-serif',
    // Keep legacy names for compatibility
    pixel: 'Orbitron, sans-serif',
    pixelBold: 'Orbitron, sans-serif',
    mono: 'Rajdhani, sans-serif',
  },

  // Font sizes (from style guide)
  sizes: {
    title: '40px',           // H1 - 2.5rem
    titleLarge: '48px',
    heading: '24px',         // H2 - 1.5rem
    subheading: '20px',
    body: '16px',            // Body - 1rem
    bodyLarge: '18px',
    small: '14px',           // HUD labels - 0.85rem
    tiny: '12px',
    score: '32px',           // Score/Combo - 2rem

    // Numeric values for calculations
    titleNum: 40,
    headingNum: 24,
    bodyNum: 16,
    smallNum: 14,
    scoreNum: 32,
  },

  // Stroke/shadow for text contrast
  stroke: {
    color: '#02030B',        // Shadow Deep
    thickness: 3,
    thicknessThin: 2,
    thicknessThick: 4,
  },

  // Spacing
  spacing: {
    xs: 4,
    sm: 8,
    md: 16,
    lg: 24,
    xl: 32,
    xxl: 48,
  },

  // Button styling
  button: {
    padding: { x: 24, y: 12 },
    borderWidth: 2,
  },

  // Animation timing
  animation: {
    fast: 100,
    normal: 200,
    slow: 400,
  },
} as const;

// Helper to get difficulty color
export function getDifficultyColor(difficulty: string): string {
  const colors = THEME.colors.difficulty;
  return colors[difficulty as keyof typeof colors] || colors.medium;
}
